import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { finalize, tap } from 'rxjs/operators';

import { Util } from '../../common/common.util';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { PopUpService } from '../../insight/pop-up.service';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { SearchUserDialog } from '../../user/search-user.dialog';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import {
    TdComplianceReportPopulationType,
    TdComplianceReportSelectionParams,
} from '../td-compliance/td-compliance.reports.model';
import { isValidDateStruct } from '../td-daily-huddle/td-daily-huddle.reports.component';
import { TdRawAgentDataDates } from '../td-daily-huddle/td-daily-huddle.reports.model';

@Component({
    selector: '[cmpsTdComplianceSelectionReport]',
    templateUrl: './td-compliance-selection.reports.component.html',
    host: {
        class: 'cmps-reports-td-compliance-selection',
    },
})
@TakeUntilDestroy()
export class TdComplianceSelectionComponent
    implements OnDestroy, TakeUntilDestroy
{
    @Input() params: TdComplianceReportSelectionParams;
    @Input() peerGroups: IOption[] = [];
    @Input() superGroups: IOption[] = [];
    @Input() reportPopulations: IOption[] = [];
    @Input() callComplianceDates: TdRawAgentDataDates;
    @Output() generated = new EventEmitter<number>();
    i18n = TranslationKeyMap;
    TdReportPopulationType = TdComplianceReportPopulationType;

    constructor(
        public popUpService: PopUpService,
        private readonly _reportService: ReportsService,
        private readonly _userSearchDialog: SearchUserDialog,
    ) {}

    get popUpVisible(): boolean {
        return (
            this.popUpService.active &&
            this.popUpService.active.componentRef.instance instanceof
                SearchUserContainerComponent
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get minDate() {
        if (this.callComplianceDates) {
            return Util.dateToNgbDate(
                new Date(this.callComplianceDates.firstDate),
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get maxDate() {
        if (this.callComplianceDates) {
            return Util.dateToNgbDate(
                new Date(this.callComplianceDates.lastDate),
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get generateReportDisabled() {
        return (
            !this.params.populationType ||
            !this.params.date ||
            !this.params.populationID ||
            this.params.invalidUser ||
            this.params.invalidDate
        );
    }

    ngOnInit(): void {
        //
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.popUpVisible) {
            this.popUpService.hide();
        }
        this._userSearchDialog.closeAll();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectPopulationType() {
        this.params.populationID = null;
        if (
            this.params.populationType ===
            TdComplianceReportPopulationType.Individual
        ) {
            this.params = {
                ...this.params,
                populationID: this.params.defaultPopulationID,
            };
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectUser(e: MouseEvent) {
        const target = e.target as HTMLElement;
        this.params.invalidUser = false;
        this._userSearchDialog.openUsingPopUp({
            options: {
                target,
                placement: 'bottom-right',
                containerClass: 'cmps-cdk-z-index-2',
            },
            close: (user) => {
                if (user) {
                    this.params.pendingData = true;
                    this._reportService
                        .retrieveDefaultHierarchyUser(user.id)
                        .pipe(
                            tap((u) => {
                                this.params.invalidUser = u.userId !== user.id;
                                this.params.populationID = {
                                    userId: user.id,
                                    userName: `${user.firstName} ${user.lastName}`,
                                };
                            }),
                            finalize(() => {
                                this.params.pendingData = false;
                            }),
                            takeUntilDestroy(this),
                        )
                        .subscribe();
                }
            },
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectDate() {
        this.params.invalidDate = this.isInvalidDate();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGenerateReport() {
        if (this.generateReportDisabled) {
            return;
        }
        this.params.pendingData = true;
        this.params.noData = false;
        this._reportService
            .generateTdCompliance(
                typeof this.params.populationID === 'string'
                    ? parseInt(this.params.populationID)
                    : this.params.populationID.userId,
                this.params.populationType,
                Util.dateToDate(this.params.date),
            )
            .pipe(
                tap((r) => {
                    if (r) {
                        this.generated.next(r);
                    } else {
                        this.params.noData = true;
                    }
                    // eslint-disable-next-line no-return-assign
                }),
                finalize(() => (this.params.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private isInvalidDate() {
        if (isValidDateStruct(this.params.date)) {
            const date = Util.dateToDate(this.params.date);
            return (
                date > new Date(this.callComplianceDates.lastDate) ||
                date < new Date(this.callComplianceDates.firstDate)
            );
        }
        return true;
    }
}
